import { memo, useState } from 'react';
import { NodeResizer, type NodeProps, type ResizeParams } from '@xyflow/react';
import { useStore, type ImageNode as TImageNode } from '../store';

function ImageNodeImpl({ id, data, selected }: NodeProps<TImageNode>) {
  const setImageField = useStore((s) => s.setImageField);
  const [liveSize, setLiveSize] = useState<{ width: number; height?: number } | null>(null);
  const width = liveSize?.width ?? data.width ?? 260;
  const height = liveSize?.height ?? data.height;

  const onResize = (_: unknown, params: ResizeParams) => {
    setLiveSize({
      width: Math.round(params.width),
      height: Math.round(params.height),
    });
  };

  const onResizeEnd = (_: unknown, params: ResizeParams) => {
    const next = {
      width: Math.round(params.width),
      height: Math.round(params.height),
    };
    setLiveSize(null);
    setImageField(id, next);
  };

  return (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (data.src) window.open(data.src, '_blank');
      }}
      title={data.name || 'Pasted image'}
      style={{
        width,
        height,
        position: 'relative',
        border: selected ? '2px solid #2f81f7' : '1px solid rgba(15, 23, 42, 0.14)',
        borderRadius: 4,
        background: 'var(--panel)',
        boxShadow: selected
          ? '0 0 0 3px rgba(47, 129, 247, 0.22), 0 4px 10px rgba(15, 23, 42, 0.08)'
          : '0 1px 3px rgba(15, 23, 42, 0.08)',
        cursor: 'move',
        overflow: 'hidden',
      }}
    >
      <NodeResizer
        isVisible={selected}
        minWidth={40}
        minHeight={40}
        keepAspectRatio
        onResize={onResize}
        onResizeEnd={onResizeEnd}
        lineStyle={{ borderColor: '#2f81f7' }}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: 999,
          border: '2px solid #fff',
          background: '#2f81f7',
        }}
      />
      {data.src ? (
        <img
          src={data.src}
          alt={data.name || 'Pasted image'}
          draggable={false}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: '100%',
            color: 'var(--text-muted)',
            fontSize: 12,
          }}
        >
          Missing image
        </div>
      )}
    </div>
  );
}

export const ImageNode = memo(ImageNodeImpl);
